import { Dispatch, SetStateAction } from 'react';

export interface FormData {
  name: string;
  noradId: string;
  tleTitle: string;
  tleFirst: string;
  tleSecond: string;
  centerFrequency: string;
  modulation: string | number;
  modulationName : string;
  bitRate: string | number;
  polarization: string | number;
  polarizationName: string |number;
  convolutionalCode: string | number;
  convolutionalCodeName: string;
  reedSolomonCode: string | number;
  reedSolomonCodeName: string;
  subcarrierFrequency: string;
  pcmCoding: string | number;
  pcmCodingName: string;
  framesyncword: string;
  framesize: string | number;
  syn: number;
  ctl: number;
  lts: number;
  slip: number;
  other: string;
  // Uplink 전용 필드
  pcmCode?: string;
  idlePattern?: string;
  idlePatternLength?: string | number;
  preambleLength?: string | number;
  carrierFrequencyMhz?: string | number;
  operatingMode?: string;
  ifCarrierLevel?: string | number;
  sweepRate?: string | number;
  sweepRange?: string | number;
}

type ValidationFunction = (value: any) => boolean;
type ErrorMessageFunction = (value: any) => string;

interface ValidationRule {
  validate: ValidationFunction;
  errorMessage: ErrorMessageFunction;
}

export const validationRules: Record<keyof FormData, ValidationRule> = {
  name: {
    validate: (value) => !!value,
    errorMessage: () => 'Name is required.',
  },
  noradId: {
    validate: (value) => !!value,
    errorMessage: () => 'NORAD ID is required.',
  },
  tleTitle: {
    validate: (value) => {
      if (typeof value === 'string') {
        return value.trim().length > 0;
      }
      return !!value;
    },
    errorMessage: () => 'TLE Title is required.',
  },
  tleFirst: {
    validate: (value) => {
      if (typeof value === 'string') {
        return value.trim().length > 0;
      }
      return !!value;
    },
    errorMessage: () => 'TLE Line1 is required.',
  },
  tleSecond: {
    validate: (value) => {
      if (typeof value === 'string') {
        return value.trim().length > 0;
      }
      return !!value;
    },
    errorMessage: () => 'TLE Line2 is required.',
  },
  centerFrequency: {
    validate: (value) => {
      const freq = Number(value.replace(/,/g, ''));
      // Uplink: 2025~2120, Downlink: 2200~2300 공통 범위: 2025~2300
      return !isNaN(freq) && freq >= 2025 && freq <= 2300;
    },
    errorMessage: () => 'Center Frequency must be between 2025 ~ 2300 MHz (Uplink: 2025~2120, Downlink: 2200~2300)',
  },
  bitRate: {
    validate: (value) => {
      const rate = Number(value.replace(/,/g, ''));
      // Uplink: 100~1,000,000, Downlink: 1,000~40,000,000 공통 범위: 100~40,000,000
      return !isNaN(rate) && rate >= 100 && rate <= 40000000;
    },
    errorMessage: () => 'Please enter a number between 100 bps and 40,000,000 bps.',
  },
  subcarrierFrequency: {
    validate: (value) => {
      if (value.trim() === '') return false; // 빈 입력 처리
      const freq = Number(value.replace(/,/g, ''));
      return !isNaN(freq) && freq >= 0 && freq <= 2000000;
    },
    errorMessage: () => 'High bandwidth with sub-carrier: 0 to 2,000,000 (in Hz)',
  },
  framesyncword: {
    validate: (value) => !!value,
    errorMessage: () => 'Sync Word is required.',
  },
  framesize: {
    validate: (value) => {
      const size = Number(value.replace(/,/g, ''));
      return !isNaN(size) && size >= 10 && size <= 32000;
    },
    errorMessage: () => 'Please enter a number between 10 and 32000 bytes.',
  },
  syn: {
    validate: (value) => Number(value) >= 0 && Number(value) <= 7,
    errorMessage: () => 'Please enter a number between 0 and 7',
  },
  ctl: {
    validate: (value) => Number(value) >= 0 && Number(value) <= 7,
    errorMessage: () => 'Please enter a number between 0 and 7',
  },
  lts: {
    validate: (value) => Number(value) >= 0 && Number(value) <= 7,
    errorMessage: () => 'Please enter a number between 0 and 7',
  },
  slip: {
    validate: (value) => Number(value) >= 0 && Number(value) <= 7,
    errorMessage: () => 'Please enter a number between 0 and 7',
  },
  modulation: {
    validate: () => true,
    errorMessage: () => '',
  },
  modulationName: {
    validate: () => true,
    errorMessage: () => '',
  },
  polarization: {
    validate: () => true,
    errorMessage: () => '',
  },
  polarizationName: {
    validate: () => true,
    errorMessage: () => '',
  },
  convolutionalCode: {
    validate: () => true,
    errorMessage: () => '',
  },
  convolutionalCodeName: {
    validate: () => true,
    errorMessage: () => '',
  },
  reedSolomonCode: {
    validate: () => true,
    errorMessage: () => '',
  },
  reedSolomonCodeName: {
    validate: () => true,
    errorMessage: () => '',
  },
  pcmCoding: {
    validate: () => true,
    errorMessage: () => '',
  },
  pcmCodingName: {
    validate: () => true,
    errorMessage: () => '',
  },
  other: {
    validate: () => true,
    errorMessage: () => '',
  },
  // Uplink 전용 필드 검증 규칙
  pcmCode: {
    validate: () => true,
    errorMessage: () => '',
  },
  idlePattern: {
    validate: () => true,
    errorMessage: () => '',
  },
  idlePatternLength: {
    validate: (value) => {
      if (value === '' || value === undefined || value === null) return true; // optional
      const length = Number(value);
      return !isNaN(length) && length >= 0 && length <= 32;
    },
    errorMessage: () => 'Idle Pattern Length must be between 0 and 32',
  },
  preambleLength: {
    validate: (value) => {
      if (value === '' || value === undefined || value === null) return true; // optional
      const length = Number(String(value).replace(/,/g, ''));
      return !isNaN(length) && length >= 0 && length <= 16777214;
    },
    errorMessage: () => 'Preamble Length must be between 0 and 16,777,214',
  },
  carrierFrequencyMhz: {
    validate: (value) => {
      if (value === '' || value === undefined || value === null) return true; // optional
      // 쉼표 제거 후 숫자로 변환
      const freq = Number(String(value).replace(/,/g, ''));
      return !isNaN(freq) && freq >= 2025 && freq <= 2120;
    },
    errorMessage: () => 'Carrier Frequency must be between 2025 and 2120 MHz',
  },
  operatingMode: {
    validate: () => true,
    errorMessage: () => '',
  },
  ifCarrierLevel: {
    validate: (value) => {
      if (value === '' || value === undefined || value === null) return true; // optional
      const level = Number(value);
      return !isNaN(level) && level >= -80.0 && level <= 10.0;
    },
    errorMessage: () => 'IF Carrier Level must be between -80.0 and 10.0 dB',
  },
  sweepRate: {
    validate: (value) => {
      if (value === '' || value === undefined || value === null) return true; // optional
      const rate = Number(value);
      return !isNaN(rate) && rate >= 0.0001 && rate <= 175000.0;
    },
    errorMessage: () => 'Sweep Rate must be between 0.0001 and 175,000.0',
  },
  sweepRange: {
    validate: (value) => {
      if (value === '' || value === undefined || value === null) return true; // optional
      // 쉼표 제거 후 숫자로 변환
      const range = Number(String(value).replace(/,/g, ''));
      return !isNaN(range) && range >= 1000 && range <= 1000000;
    },
    errorMessage: () => 'Sweep Range must be between 1,000 and 1,000,000',
  },
};

export const validateField = (
  field: keyof FormData,
  value: any,
  setErrors: Dispatch<SetStateAction<Partial<Record<keyof FormData, string>>>>
): boolean => {
  const rule = validationRules[field];
  const isValid = rule.validate(value);
  
  setErrors(prev => ({
    ...prev,
    [field]: isValid ? '' : rule.errorMessage(value)
  }));

  return isValid;
};

export const validateForm = (
  formData: FormData,
  setErrors: Dispatch<SetStateAction<Partial<Record<keyof FormData, string>>>>
): boolean => {
  let isValid = true;
  
  Object.keys(formData).forEach((field) => {
    const key = field as keyof FormData;
    if (!validateField(key, formData[key], setErrors)) {
      isValid = false;
    }
  });

  return isValid;
};

export const formatNumber = (num: string): string => {
  const cleanNum = num.replace(/,/g, '');
  const parts = cleanNum.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.join('.');
};